
import React, { useState } from 'react';
import { useAppContext } from '../App';
import { Calendar, MapPin, Users, Plus, X, User as UserIcon, CreditCard, CheckCircle, Ticket, AlertTriangle, Clock, Check } from 'lucide-react';
import { UserRole, ChurchEvent } from '../types';
import { checkContentSafety } from '../services/geminiService';
import SEO from './SEO';

const EventsPage: React.FC = () => {
    const { events, churches, currentUser, addEvent, registerForEvent, joinedEventIds, t } = useAppContext();
    const [showModal, setShowModal] = useState(false);
    const [showPaymentModal, setShowPaymentModal] = useState(false);
    
    // Details Modal State
    const [showDetailsModal, setShowDetailsModal] = useState(false);
    const [selectedEvent, setSelectedEvent] = useState<ChurchEvent | null>(null);
    
    // Notifications
    const [notification, setNotification] = useState<{type: 'success' | 'error', message: string} | null>(null);
    
    const [error, setError] = useState<string | null>(null);
    const [formData, setFormData] = useState({
        title: '',
        description: '',
        date: '',
        time: '',
        location: '',
        guestSpeakers: '',
        price: ''
    });

    const showNotification = (type: 'success' | 'error', message: string) => {
        setNotification({ type, message });
        setTimeout(() => setNotification(null), 4000);
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!currentUser?.churchId) return;
        setError(null);

        // Safety Check
        const safetyCheck = await checkContentSafety(`${formData.title} ${formData.description}`);
        if (!safetyCheck.safe) {
            setError(`Event flagged: ${safetyCheck.reason}`);
            return;
        }

        const newEvent: ChurchEvent = {
            id: Date.now().toString(),
            churchId: currentUser.churchId,
            title: formData.title,
            description: formData.description,
            date: `${formData.date}T${formData.time}`,
            location: formData.location,
            guestSpeakers: formData.guestSpeakers.split(',').map(s => s.trim()).filter(s => s),
            attendeeCount: 0,
            imageUrl: `https://picsum.photos/seed/${Date.now()}/800/400`,
            price: formData.price ? parseFloat(formData.price) : 0
        };

        addEvent(newEvent);
        setShowModal(false);
        setFormData({ title: '', description: '', date: '', time: '', location: '', guestSpeakers: '', price: '' });
        showNotification('success', 'Event created successfully!');
    };

    const handleRegisterClick = async (event: ChurchEvent) => {
        if (event.price && event.price > 0) {
            setSelectedEvent(event);
            setShowPaymentModal(true);
        } else {
            // Free Event - Direct Register
            const result = await registerForEvent(event.id);
            if (result.success) {
                showNotification('success', result.message);
            } else {
                showNotification('error', result.message);
            }
        }
    };

    const handlePaymentSuccess = async () => {
        if (selectedEvent) {
            const result = await registerForEvent(selectedEvent.id);
            if (result.success) {
                showNotification('success', `Payment successful! ${result.message}`);
            } else {
                showNotification('error', result.message);
            }
            setShowPaymentModal(false);
            setSelectedEvent(null);
        }
    };

    const handleShowDetails = (event: ChurchEvent) => {
        setSelectedEvent(event);
        setShowDetailsModal(true);
    };

    // Schema.org for list of events
    const eventSchemas = events.map(event => ({
        "@context": "https://schema.org",
        "@type": "Event",
        "name": event.title,
        "startDate": event.date,
        "location": {
            "@type": "Place",
            "name": event.location,
            "address": event.location
        },
        "image": event.imageUrl,
        "description": event.description,
        "organizer": {
            "@type": "Organization",
            "name": churches.find(c => c.id === event.churchId)?.name || "Holyness Church"
        },
        "offers": {
            "@type": "Offer",
            "price": event.price || 0,
            "priceCurrency": "USD"
        }
    }));

    return (
        <div className="space-y-6 relative">
            <SEO 
                title="Christian Events & Gatherings | Holyness" 
                description="Discover upcoming worship nights, conferences, and community gatherings near you."
                path="/events"
                schema={eventSchemas}
            />

            {/* Global Notification Toast */}
            {notification && (
                <div className={`fixed top-24 right-4 z-50 px-6 py-4 rounded-lg shadow-xl flex items-center gap-3 animate-fadeIn ${
                    notification.type === 'success' ? 'bg-green-600 text-white' : 'bg-red-600 text-white'
                }`}>
                    {notification.type === 'success' ? <CheckCircle className="w-6 h-6" /> : <AlertTriangle className="w-6 h-6" />}
                    <p className="font-medium">{notification.message}</p>
                    <button onClick={() => setNotification(null)} className="ml-2 hover:bg-white/20 rounded p-1"><X className="w-4 h-4" /></button>
                </div>
            )}

            <div className="flex justify-between items-center bg-white p-6 rounded-xl shadow-sm border border-gray-100">
                <div>
                    <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
                        <Calendar className="text-blue-600" /> {t('kingdom_gatherings')}
                    </h1>
                    <p className="text-gray-500 mt-1">{t('discover_events')}</p>
                </div>
                
                {currentUser?.role === UserRole.PASTOR && (
                    <button 
                        onClick={() => setShowModal(true)}
                        className="bg-blue-600 text-white px-4 py-2 rounded-lg font-bold hover:bg-blue-700 flex items-center gap-2 shadow-sm transition"
                    >
                        <Plus className="w-5 h-5" /> {t('host_gathering')}
                    </button>
                )}
            </div>

            <div className="grid gap-6">
                {events.map(event => {
                    const hostChurch = churches.find(c => c.id === event.churchId);
                    const eventDate = new Date(event.date);
                    const isPaid = event.price && event.price > 0;
                    const isRegistered = joinedEventIds.includes(event.id);
                    
                    return (
                        <div key={event.id} className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden flex flex-col md:flex-row group hover:shadow-md transition">
                            <div className="md:w-1/3 h-48 md:h-auto relative overflow-hidden">
                                <img src={event.imageUrl} alt={event.title} className="w-full h-full object-cover group-hover:scale-105 transition duration-500" />
                                <div className="absolute top-4 left-4 bg-white/90 backdrop-blur rounded-lg px-3 py-1 text-center shadow-lg">
                                    <div className="text-xs font-bold text-red-500 uppercase">{eventDate.toLocaleString('default', { month: 'short' })}</div>
                                    <div className="text-xl font-bold text-gray-900">{eventDate.getDate()}</div>
                                </div>
                                <div className="absolute bottom-4 right-4">
                                     {isPaid ? (
                                         <span className="bg-blue-600 text-white px-3 py-1 rounded-full text-xs font-bold shadow-md">
                                             ${event.price}
                                         </span>
                                     ) : (
                                         <span className="bg-green-500 text-white px-3 py-1 rounded-full text-xs font-bold shadow-md">
                                             {t('free')}
                                         </span>
                                     )}
                                </div>
                            </div>
                            <div className="p-6 md:w-2/3 flex flex-col justify-between">
                                <div>
                                    <div className="flex justify-between items-start mb-2">
                                        <h3 className="text-xl font-bold text-gray-900">{event.title}</h3>
                                        {hostChurch && (
                                            <span className="text-xs font-semibold bg-blue-50 text-blue-700 px-2 py-1 rounded-full">
                                                {t('hosted_by')} {hostChurch.name}
                                            </span>
                                        )}
                                    </div>
                                    
                                    <div className="flex flex-col sm:flex-row gap-4 text-sm text-gray-500 mb-4">
                                        <div className="flex items-center gap-1">
                                            <Clock className="w-4 h-4" />
                                            {eventDate.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                                        </div>
                                        <div className="flex items-center gap-1">
                                            <MapPin className="w-4 h-4" />
                                            {event.location}
                                        </div>
                                        <div className="flex items-center gap-1">
                                            <Users className="w-4 h-4" />
                                            {event.attendeeCount} going
                                        </div>
                                    </div>

                                    <p className="text-gray-600 mb-4 line-clamp-2">{event.description}</p>

                                    {event.guestSpeakers && event.guestSpeakers.length > 0 && (
                                        <div className="mb-4">
                                            <p className="text-xs font-bold text-gray-500 uppercase mb-1">{t('guest_speakers')}</p>
                                            <div className="flex flex-wrap gap-2">
                                                {event.guestSpeakers.map((speaker, idx) => (
                                                    <span key={idx} className="flex items-center gap-1 text-xs bg-gray-100 text-gray-700 px-2 py-1 rounded border border-gray-200">
                                                        <UserIcon className="w-3 h-3" /> {speaker}
                                                    </span>
                                                ))}
                                            </div>
                                        </div>
                                    )}
                                </div>
                                
                                <div className="flex gap-3 mt-4 pt-4 border-t border-gray-100">
                                    <button 
                                        onClick={() => !isRegistered && handleRegisterClick(event)}
                                        disabled={isRegistered}
                                        className={`flex-1 text-white py-2 rounded-lg font-bold transition flex items-center justify-center gap-2 ${
                                            isRegistered 
                                            ? 'bg-gray-400 cursor-not-allowed' 
                                            : isPaid 
                                                ? 'bg-blue-600 hover:bg-blue-700' 
                                                : 'bg-green-600 hover:bg-green-700'
                                        }`}
                                    >
                                        {isRegistered ? (
                                            <><Check className="w-4 h-4" /> Registered</>
                                        ) : isPaid ? (
                                            <><CreditCard className="w-4 h-4" /> {t('register_pay')} ${event.price}</>
                                        ) : (
                                            <><CheckCircle className="w-4 h-4" /> {t('rsvp_now')}</>
                                        )}
                                    </button>
                                    <button 
                                        onClick={() => handleShowDetails(event)}
                                        className="px-4 py-2 border border-gray-300 rounded-lg text-gray-600 font-medium hover:bg-gray-50 transition"
                                    >
                                        Details
                                    </button>
                                </div>
                            </div>
                        </div>
                    );
                })}
            </div>

            {/* Create Event Modal */}
            {showModal && (
                <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
                    <div className="bg-white rounded-xl shadow-2xl max-w-lg w-full p-6 animate-fadeIn max-h-[90vh] overflow-y-auto">
                        <div className="flex justify-between items-center mb-6 border-b pb-4">
                            <h3 className="text-xl font-bold text-gray-900">Host a Kingdom Gathering</h3>
                            <button onClick={() => setShowModal(false)} className="text-gray-400 hover:text-gray-600"><X /></button>
                        </div>
                        
                        {error && <div className="mb-4 text-xs text-red-600 bg-red-50 p-2 rounded flex items-center gap-1"><AlertTriangle className="w-4 h-4"/> {error}</div>}

                        <form onSubmit={handleSubmit} className="space-y-4">
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">Event Title</label>
                                <input 
                                    required
                                    type="text" 
                                    className="w-full bg-transparent border-b border-gray-300 rounded-none px-2 py-2 focus:outline-none focus:border-blue-600 transition-colors placeholder-gray-400"
                                    value={formData.title}
                                    onChange={e => setFormData({...formData, title: e.target.value})}
                                    placeholder="e.g., Regional Worship Night"
                                />
                            </div>

                            <div className="grid grid-cols-2 gap-4">
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">Date</label>
                                    <input 
                                        required
                                        type="date" 
                                        className="w-full bg-transparent border-b border-gray-300 rounded-none px-2 py-2 focus:outline-none focus:border-blue-600 transition-colors"
                                        value={formData.date}
                                        onChange={e => setFormData({...formData, date: e.target.value})}
                                    />
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">Time</label>
                                    <input 
                                        required
                                        type="time" 
                                        className="w-full bg-transparent border-b border-gray-300 rounded-none px-2 py-2 focus:outline-none focus:border-blue-600 transition-colors"
                                        value={formData.time}
                                        onChange={e => setFormData({...formData, time: e.target.value})}
                                    />
                                </div>
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">Location</label>
                                <input 
                                    required
                                    type="text" 
                                    className="w-full bg-transparent border-b border-gray-300 rounded-none px-2 py-2 focus:outline-none focus:border-blue-600 transition-colors placeholder-gray-400"
                                    value={formData.location}
                                    onChange={e => setFormData({...formData, location: e.target.value})}
                                    placeholder="Address or Venue Name"
                                />
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">Registration Cost ($)</label>
                                <input 
                                    type="number" 
                                    min="0"
                                    step="0.01"
                                    className="w-full bg-transparent border-b border-gray-300 rounded-none px-2 py-2 focus:outline-none focus:border-blue-600 transition-colors placeholder-gray-400"
                                    value={formData.price}
                                    onChange={e => setFormData({...formData, price: e.target.value})}
                                    placeholder="0 for Free Event"
                                />
                                <p className="text-xs text-gray-500 mt-1">Leave empty or 0 for free admission.</p>
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
                                <textarea 
                                    required
                                    className="w-full bg-transparent border-b border-gray-300 rounded-none px-2 py-2 focus:outline-none focus:border-blue-600 transition-colors h-24 resize-none placeholder-gray-400"
                                    value={formData.description}
                                    onChange={e => setFormData({...formData, description: e.target.value})}
                                    placeholder="Describe the event..."
                                />
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">Guest Pastors / Speakers</label>
                                <input 
                                    type="text" 
                                    className="w-full bg-transparent border-b border-gray-300 rounded-none px-2 py-2 focus:outline-none focus:border-blue-600 transition-colors placeholder-gray-400"
                                    value={formData.guestSpeakers}
                                    onChange={e => setFormData({...formData, guestSpeakers: e.target.value})}
                                    placeholder="Comma separated names (e.g., Pastor John, Rev. Smith)"
                                />
                            </div>

                            <button type="submit" className="w-full bg-blue-600 text-white py-3 rounded-lg font-bold hover:bg-blue-700 mt-4">
                                Announce Event
                            </button>
                        </form>
                    </div>
                </div>
            )}

            {/* Details Modal */}
            {showDetailsModal && selectedEvent && (
                <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4">
                    <div className="bg-white rounded-xl shadow-2xl max-w-lg w-full p-6 animate-fadeIn max-h-[90vh] overflow-y-auto">
                        <div className="flex justify-between items-center mb-6 border-b pb-4">
                            <h3 className="text-xl font-bold text-gray-900">{selectedEvent.title}</h3>
                            <button onClick={() => { setShowDetailsModal(false); setSelectedEvent(null); }} className="text-gray-400 hover:text-gray-600"><X /></button>
                        </div>
                        
                        <div className="space-y-6">
                            <img src={selectedEvent.imageUrl} alt={selectedEvent.title} className="w-full h-48 object-cover rounded-lg" />
                            
                            <div className="flex flex-col gap-3">
                                <div className="flex items-center gap-2 text-gray-700">
                                    <Calendar className="w-5 h-5 text-blue-600" />
                                    <span>{new Date(selectedEvent.date).toLocaleDateString(undefined, { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })} at {new Date(selectedEvent.date).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}</span>
                                </div>
                                <div className="flex items-center gap-2 text-gray-700">
                                    <MapPin className="w-5 h-5 text-blue-600" />
                                    <span>{selectedEvent.location}</span>
                                </div>
                                <div className="flex items-center gap-2 text-gray-700">
                                    <Users className="w-5 h-5 text-blue-600" />
                                    <span>{selectedEvent.attendeeCount} people attending</span>
                                </div>
                            </div>

                            <div>
                                <h4 className="font-bold text-gray-900 mb-2">About this Event</h4>
                                <p className="text-gray-600 leading-relaxed">{selectedEvent.description}</p>
                            </div>

                            {selectedEvent.guestSpeakers && selectedEvent.guestSpeakers.length > 0 && (
                                <div>
                                    <h4 className="font-bold text-gray-900 mb-2">Guest Speakers</h4>
                                    <div className="flex flex-wrap gap-2">
                                        {selectedEvent.guestSpeakers.map((speaker, idx) => (
                                            <span key={idx} className="bg-gray-100 text-gray-700 px-3 py-1 rounded-full text-sm">
                                                {speaker}
                                            </span>
                                        ))}
                                    </div>
                                </div>
                            )}

                            <div className="pt-4 border-t border-gray-100">
                                <button 
                                    onClick={() => { 
                                        setShowDetailsModal(false); 
                                        if(!joinedEventIds.includes(selectedEvent.id)) handleRegisterClick(selectedEvent); 
                                    }}
                                    disabled={joinedEventIds.includes(selectedEvent.id)}
                                    className={`w-full text-white py-3 rounded-lg font-bold transition ${joinedEventIds.includes(selectedEvent.id) ? 'bg-gray-400 cursor-not-allowed' : 'bg-blue-600 hover:bg-blue-700'}`}
                                >
                                    {joinedEventIds.includes(selectedEvent.id) 
                                        ? "Already Registered" 
                                        : (selectedEvent.price && selectedEvent.price > 0 ? `Register ($${selectedEvent.price})` : "RSVP Now")
                                    }
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Payment Modal */}
            {showPaymentModal && selectedEvent && (
                 <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4">
                    <div className="bg-white rounded-xl shadow-2xl max-w-md w-full animate-fadeIn overflow-hidden">
                        <div className="bg-gradient-to-r from-blue-600 to-indigo-700 p-6 text-white relative overflow-hidden">
                             <div className="absolute top-0 right-0 w-32 h-32 bg-white/10 rounded-full blur-2xl -mr-10 -mt-10"></div>
                             <h3 className="text-xl font-bold flex items-center gap-2 relative z-10"><Ticket className="w-6 h-6"/> Event Registration</h3>
                             <p className="text-blue-100 mt-1 relative z-10">{selectedEvent.title}</p>
                        </div>
                        
                        <div className="p-6">
                            <div className="flex justify-between items-center mb-6">
                                <span className="text-gray-500 font-medium">Total Amount</span>
                                <span className="text-3xl font-bold text-gray-900">${selectedEvent.price}</span>
                            </div>

                            <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 mb-6">
                                <label className="block text-xs font-bold text-gray-500 uppercase mb-2">Card Details</label>
                                <div className="flex items-center gap-2 bg-white border border-gray-300 rounded px-3 py-2 mb-3">
                                    <CreditCard className="w-4 h-4 text-gray-400" />
                                    <input type="text" placeholder="Card number" className="flex-1 outline-none text-sm" />
                                </div>
                                <div className="flex gap-3">
                                    <input type="text" placeholder="MM/YY" className="w-1/2 bg-white border border-gray-300 rounded px-3 py-2 outline-none text-sm" />
                                    <input type="text" placeholder="CVC" className="w-1/2 bg-white border border-gray-300 rounded px-3 py-2 outline-none text-sm" />
                                </div>
                            </div>
                            
                            <div className="flex items-center gap-2 text-xs text-gray-500 mb-6 justify-center">
                                <div className="w-2 h-2 bg-green-500 rounded-full"></div>
                                Secure Payment via Stripe Integration
                            </div>

                            <div className="flex gap-3">
                                <button 
                                    onClick={() => setShowPaymentModal(false)} 
                                    className="flex-1 py-3 border border-gray-300 rounded-lg font-bold text-gray-700 hover:bg-gray-50 transition"
                                >
                                    Cancel
                                </button>
                                <button 
                                    onClick={handlePaymentSuccess}
                                    className="flex-1 py-3 bg-blue-600 text-white rounded-lg font-bold hover:bg-blue-700 shadow-lg transition"
                                >
                                    Pay & Register
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default EventsPage;
